// src/products/dataroom/services/partyTeamService.js
import { supabase } from '../../../config';

class PartyTeamService {
  // ============ PARTY MANAGEMENT ============

  /**
   * Get all parties across all projects (for global parties page)
   */
  async getAllParties() {
    try {
      console.log('partyTeamService.getAllParties called');
      const { data, error } = await supabase
        .from('dataroom_parties')
        .select('*')
        .order('created_at', { ascending: false });
      
      console.log('Supabase getAllParties result:', { data, error });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching all parties:', error);
      return { data: null, error };
    }
  }

  /**
   * Get all parties for a project
   */
  async getParties(projectId) {
    try {
      console.log('partyTeamService.getParties called with projectId:', projectId);
      const { data, error } = await supabase
        .from('dataroom_parties')
        .select(`
          *,
          dataroom_teams (
            id,
            name,
            description,
            team_type,
            created_at
          ),
          dataroom_user_parties (
            user_id,
            assigned_at
          )
        `)
        .eq('project_id', projectId)
        .order('name');
      
      console.log('Supabase query result:', { data, error });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching parties:', error);
      return { data: null, error };
    }
  }

  /**
   * Create a new party
   */
  async createParty(projectId, partyData) {
    try {
      const { data, error } = await supabase
        .from('dataroom_parties')
        .insert({
          project_id: projectId,
          name: partyData.name,
          code: partyData.code,
          color: partyData.color,
          description: partyData.description,
          party_type: partyData.party_type || 'custom'
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating party:', error);
      return { data: null, error };
    }
  }

  /**
   * Update a party
   */
  async updateParty(partyId, partyData) {
    try {
      const { data, error } = await supabase
        .from('dataroom_parties')
        .update({
          name: partyData.name,
          description: partyData.description,
          party_type: partyData.party_type
        })
        .eq('id', partyId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating party:', error);
      return { data: null, error };
    }
  }

  /**
   * Delete a party
   */
  async deleteParty(partyId) {
    try {
      const { error } = await supabase
        .from('dataroom_parties')
        .delete()
        .eq('id', partyId);

      if (error) throw error;
      return { data: true, error: null };
    } catch (error) {
      console.error('Error deleting party:', error);
      return { data: null, error };
    }
  }

  // ============ TEAM MANAGEMENT ============

  /**
   * Get all teams across all parties (for global teams page)
   */
  async getAllTeams() {
    try {
      console.log('partyTeamService.getAllTeams called');
      const { data, error } = await supabase
        .from('dataroom_teams')
        .select(`
          *,
          dataroom_parties!inner(
            id,
            name,
            code,
            color,
            project_id
          )
        `)
        .order('created_at', { ascending: false });
      
      console.log('Supabase getAllTeams result:', { data, error });
      if (error) throw error;
      
      // Transform data to include party information in the team object
      const transformedData = data?.map(team => ({
        ...team,
        party_name: team.dataroom_parties.name,
        party_code: team.dataroom_parties.code,
        party_color: team.dataroom_parties.color,
        project_id: team.dataroom_parties.project_id
      })) || [];
      
      return { data: transformedData, error: null };
    } catch (error) {
      console.error('Error fetching all teams:', error);
      return { data: null, error };
    }
  }

  /**
   * Get all teams for a party
   */
  async getTeams(partyId) {
    try {
      const { data, error } = await supabase
        .from('dataroom_teams')
        .select(`
          *,
          dataroom_user_teams (
            user_id,
            assigned_at
          )
        `)
        .eq('party_id', partyId)
        .order('name');

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching teams:', error);
      return { data: null, error };
    }
  }

  /**
   * Create a new team
   */
  async createTeam(partyId, teamData) {
    try {
      // Get party info to create team code with party prefix
      const { data: party } = await supabase
        .from('dataroom_parties')
        .select('code')
        .eq('id', partyId)
        .single();

      // Generate a team code that fits within 10 characters
      let teamCode;
      if (party?.code) {
        // Party code is 3 chars, so we have 7 chars left (minus 1 for separator = 6 chars)
        const partyCode = party.code.substring(0, 3); // Ensure party code is max 3 chars
        const teamSuffix = teamData.code ? teamData.code.substring(0, 6) : `T${Date.now().toString().slice(-5)}`;
        teamCode = `${partyCode}-${teamSuffix}`;
      } else {
        // Fallback if no party code
        teamCode = teamData.code ? teamData.code.substring(0, 10) : `T${Date.now().toString().slice(-9)}`;
      }

      const { data, error } = await supabase
        .from('dataroom_teams')
        .insert({
          party_id: partyId,
          name: teamData.name,
          code: teamCode,
          description: teamData.description,
          team_type: teamData.team_type || 'general'
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating team:', error);
      return { data: null, error };
    }
  }

  /**
   * Update a team
   */
  async updateTeam(teamId, teamData) {
    try {
      const { data, error } = await supabase
        .from('dataroom_teams')
        .update({
          name: teamData.name,
          description: teamData.description,
          team_type: teamData.team_type
        })
        .eq('id', teamId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating team:', error);
      return { data: null, error };
    }
  }

  /**
   * Delete a team
   */
  async deleteTeam(teamId) {
    try {
      const { error } = await supabase
        .from('dataroom_teams')
        .delete()
        .eq('id', teamId);

      if (error) throw error;
      return { data: true, error: null };
    } catch (error) {
      console.error('Error deleting team:', error);
      return { data: null, error };
    }
  }

  // ============ USER ASSIGNMENTS ============

  /**
   * Assign user to a party
   */
  async assignUserToParty(userId, projectId, partyId, assignedBy) {
    try {
      const { data, error } = await supabase
        .from('dataroom_user_parties')
        .insert({
          user_id: userId,
          project_id: projectId,
          party_id: partyId,
          assigned_by: assignedBy
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error assigning user to party:', error);
      return { data: null, error };
    }
  }

  /**
   * Assign user to a team
   */
  async assignUserToTeam(userId, projectId, teamId, assignedBy) {
    try {
      const { data, error } = await supabase
        .from('dataroom_user_teams')
        .insert({
          user_id: userId,
          project_id: projectId,
          team_id: teamId,
          assigned_by: assignedBy
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error assigning user to team:', error);
      return { data: null, error };
    }
  }

  /**
   * Remove user from a party
   */
  async removeUserFromParty(userId, projectId, partyId) {
    try {
      const { error } = await supabase
        .from('dataroom_user_parties')
        .delete()
        .eq('user_id', userId)
        .eq('project_id', projectId)
        .eq('party_id', partyId);

      if (error) throw error;
      return { data: true, error: null };
    } catch (error) {
      console.error('Error removing user from party:', error);
      return { data: null, error };
    }
  }

  /**
   * Remove user from a team
   */
  async removeUserFromTeam(userId, projectId, teamId) {
    try {
      const { error } = await supabase
        .from('dataroom_user_teams')
        .delete()
        .eq('user_id', userId)
        .eq('project_id', projectId)
        .eq('team_id', teamId);

      if (error) throw error;
      return { data: true, error: null };
    } catch (error) {
      console.error('Error removing user from team:', error);
      return { data: null, error };
    }
  }

  /**
   * Get user's parties and teams for a project
   */
  async getUserPartyTeams(userId, projectId) {
    try {
      const { data, error } = await supabase
        .from('dataroom_user_parties')
        .select(`
          party_id,
          dataroom_parties (
            id,
            name,
            description,
            party_type
          ),
          dataroom_user_teams (
            team_id,
            dataroom_teams (
              id,
              name,
              description,
              team_type
            )
          )
        `)
        .eq('user_id', userId)
        .eq('project_id', projectId);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching user party teams:', error);
      return { data: null, error };
    }
  }

  // ============ FILE PERMISSIONS ============

  /**
   * Set file permissions for a team
   */
  async setTeamFilePermissions(itemId, teamId, permissionType) {
    try {
      const { data, error } = await supabase
        .from('dataroom_file_permissions')
        .upsert({
          dataroom_item_id: itemId,
          team_id: teamId,
          permission_type: permissionType
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error setting team file permissions:', error);
      return { data: null, error };
    }
  }

  /**
   * Get file permissions for a dataroom item
   */
  async getFilePermissions(itemId) {
    try {
      const { data, error } = await supabase
        .from('dataroom_file_permissions')
        .select(`
          *,
          dataroom_teams (
            id,
            name,
            dataroom_parties (
              id,
              name
            )
          )
        `)
        .eq('dataroom_item_id', itemId);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching file permissions:', error);
      return { data: null, error };
    }
  }

  // ============ UTILITY METHODS ============

  /**
   * Get predefined party types
   */
  getPredefinedPartyTypes() {
    return [
      { id: 'strategic_bidder', name: 'Strategic Bidder', description: 'Strategic acquirer or partner (e.g., Investment Firm XYZ)' },
      { id: 'financial_bidder', name: 'Financial Bidder', description: 'Financial buyer or investor group' },
      { id: 'buyer_group', name: 'Buyer Group', description: 'Consortium or buyer group' },
      { id: 'strategic_partner', name: 'Strategic Partner', description: 'Strategic partner or joint venture partner' },
      { id: 'custom', name: 'Custom Party', description: 'Custom bidding organization' }
    ];
  }

  /**
   * Get predefined team types
   */
  getPredefinedTeamTypes() {
    return [
      { id: 'deal_lead', name: 'Deal Lead', description: 'Deal leads and management team' },
      { id: 'legal_team', name: 'Legal Team', description: 'Legal counsel and lawyers' },
      { id: 'finance_team', name: 'Finance Team', description: 'Financial advisors and accountants' },
      { id: 'tax_team', name: 'Tax Team', description: 'Tax advisors and specialists' },
      { id: 'technical_team', name: 'Technical Team', description: 'Technical advisors and specialists' },
      { id: 'general_access', name: 'General Access', description: 'General team access' }
    ];
  }

  /**
   * Get permission types
   */
  getPermissionTypes() {
    return [
      { id: 'none', name: 'No Access', description: 'Cannot view or access' },
      { id: 'view', name: 'View Only', description: 'Can view but not download' },
      { id: 'print', name: 'View & Print', description: 'Can view and print' },
      { id: 'download', name: 'Full Access', description: 'Can view, print, and download' }
    ];
  }
}

const partyTeamService = new PartyTeamService();
export default partyTeamService;
